import ExcelJS from "exceljs";
import eurLogo from "../../../../assets/images/logo/sanEurLogoJpeg.jpeg";
import intlLogo from "../../../../assets/images/logo/sanIntlLogoJpeg.jpeg";
import antLogo from "../../../../assets/images/logo/agronixLogoJpeg.jpeg";
import { format } from "date-fns";

/**
 * Exports each selected shipping instruction as its own printable sheet
 * with a form-like layout.
 */
const convertImageToBase64 = (url) =>
  new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.onerror = reject;
      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.send();
    } catch (err) {
      reject(err);
    }
  });

  const formatDDMMYYYY = (value) => {
  if (!value) return "";
  const d = new Date(value);          // tabloda yaptığın gibi
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "dd/MM/yyyy");
};

export async function ExportGoodsReceipt(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("No rows to export");
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "SanIntl";
  workbook.created = new Date();

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];

    const siNumber = r?.finalShippingInstructionNumber || r?.shippingInstructionNumber || "";
    const blDateRaw = r?.billOfLadingDate ?? null;
    const cmrDateRaw = r?.cmrDate ?? null;
    const arNumber = r?.accountsReceivableNumber || r?.accountsReceivable?.number || "";
    const shipmentQty = r?.shipmentRequest?.shipmentQuantity ?? r?.shipmentQuantity ?? null;
    const ataRaw = r?.actualTimeOfArrival || "";

    const ataDate = formatDDMMYYYY(ataRaw);

    let dateLabel = "BL date";
    let docDateText = "";

    if (cmrDateRaw) {
      dateLabel = "CMR date";
      docDateText = formatDDMMYYYY(cmrDateRaw);
    } else if (blDateRaw) {
      docDateText = formatDDMMYYYY(blDateRaw);
    }
    
    const name = `GoodsReceipt-${i + 1}`;
    const worksheet = workbook.addWorksheet(name, {
      pageSetup: {
        orientation: "landscape",
        printArea : `A1:H${15}`,
      },
    });

    // set columns first (layout control)
    worksheet.columns = [
      { width: 14 }, // A
      { width: 26 }, // B
      { width: 6 },  // C
      { width: 6 },  // D
      { width: 6 },  // E
      { width: 6 },  // F
      { width: 14 },  // G
      { width: 26 },  // H
    ];

    let offsetRow = 2; 
    try {
      const company =
        r?.shipmentRequest?.salesOrder?.company ||
        r?.shipmentRequest?.purchaseOrder?.company ||
        "";
      const logo = company === "SGE" ? eurLogo : company === "ANT" ? antLogo : intlLogo;
      const base64Image = await convertImageToBase64(logo);
      const base64 = typeof base64Image === "string" && base64Image.includes(",") ? base64Image.split(",")[1] : base64Image;
      const imageId = workbook.addImage({
        base64,
        extension: "jpeg",
      });
      // place logo top-left and adjust size via range
      // use rows 1..4 for logo, so data must start below row 4
      worksheet.addImage(imageId, "A1:C4");
      offsetRow = 6; // move content below logo (rows 1-4 used by logo; leave row5 gap)
    } catch (err) {
      // no logo or load failed -> keep offsetRow = 2
      offsetRow = 2;
    }

    // Row spacing relative to offsetRow
    worksheet.getRow(offsetRow).height = 18;
    worksheet.getRow(offsetRow + 2).height = 18;
    worksheet.getRow(offsetRow + 4).height = 18;

    // ST no (left top)
    worksheet.getCell(`A${offsetRow}`).value = "ST no";
    worksheet.getCell(`A${offsetRow}`).font = { bold: true };
    worksheet.getCell(`B${offsetRow}`).value = siNumber;
    worksheet.getCell(`B${offsetRow}`).font = { bold: false };

    worksheet.getCell(`G${offsetRow}`).value = dateLabel;
    worksheet.getCell(`G${offsetRow}`).font = { bold: true };
    worksheet.getCell(`H${offsetRow}`).value = docDateText;

    // AR no (left middle)
    worksheet.getCell(`A${offsetRow + 2}`).value = "AR no";
    worksheet.getCell(`A${offsetRow + 2}`).font = { bold: true };
    worksheet.getCell(`B${offsetRow + 2}`).value = arNumber;

    // net kg (right middle)
    worksheet.getCell(`G${offsetRow + 2}`).value = "Net KG";
    worksheet.getCell(`G${offsetRow + 2}`).font = { bold: true };
    if (shipmentQty !== null && shipmentQty !== "" && !isNaN(Number(shipmentQty))) {
      worksheet.getCell(`H${offsetRow + 2}`).value = Number(shipmentQty);
      worksheet.getCell(`H${offsetRow + 2}`).numFmt = "#,##0.00";
    } else {
      worksheet.getCell(`H${offsetRow + 2}`).value = "";
    }

    // ATA (left below)
    worksheet.getCell(`A${offsetRow + 4}`).value = "ATA";
    worksheet.getCell(`A${offsetRow + 4}`).font = { bold: true };
    if (ataDate) {
      worksheet.getCell(`B${offsetRow + 4}`).value = ataDate;
      worksheet.getCell(`B${offsetRow + 4}`).numFmt = "dd/mm/yyyy";
    } else {
      worksheet.getCell(`B${offsetRow + 4}`).value = "";
    }

    // Draw thin borders around label/value pairs for clarity
    const cellsToBorder = [
      `A${offsetRow}`, `B${offsetRow}`, `G${offsetRow}`, `H${offsetRow}`,
      `A${offsetRow + 2}`, `B${offsetRow + 2}`, `G${offsetRow + 2}`, `H${offsetRow + 2}`,
      `A${offsetRow + 4}`, `B${offsetRow + 4}`
    ];
    cellsToBorder.forEach((addr) => {
      const c = worksheet.getCell(addr);
      c.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      c.alignment = { vertical: "middle", horizontal: "left" };
    });

    const sigStart = offsetRow + 8; // leaves some vertical space
    const sigEnd = sigStart + 4;
    const sigCell = worksheet.getCell(`D${sigStart}`);
    sigCell.value = "Consignee Stamp & Signature";
    sigCell.alignment = { vertical: "middle", horizontal: "center" };
    sigCell.font = {size: 9, color: { argb: "FF000000" } };

    // make sure header labels are slightly dimmer and values readable
    [`A${offsetRow}`, `G${offsetRow}`, `A${offsetRow + 2}`, `G${offsetRow + 2}`, `A${offsetRow + 4}`].forEach((addr) => {
      worksheet.getCell(addr).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF2F2F2" },
      };
    });

    // Add a small note / spacing near bottom if wanted
    worksheet.getCell(`A${sigEnd + 4}`).value = "";
  }

  // write and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const fileName = `GoodsReceipt_Form_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.xlsx`;

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default ExportGoodsReceipt;